import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import ParameterFilterIcon from "./ParameterFilterIcon";
import SearchIcon from "@material-ui/icons/Search";
import VisibilityOffOutlinedIcon from "@material-ui/icons/VisibilityOffOutlined";
import DescriptionParametersConfirmDialog from "./DescriptionParametersConfirmDialog";
import styles from "./DescriptionParametersSection.module.scss";
import inputClasses from "./descriptionParameterInputClasses";
import DescriptionParameterNormalization from "./DescriptionParameterNormalization";

const valueSeparators = [",", ";", "|"];
const validValueSeparator = value =>
  value == null ||
  (value.trim().length > 0 &&
    new Blob([value]).size <= 32 &&
    !Array.from(value).some(
      character =>
        character.charCodeAt(0) < 32 ||
        (character.charCodeAt(0) >= 127 && character.charCodeAt(0) <= 159) ||
        character.charCodeAt(0) === 8232 ||
        character.charCodeAt(0) === 8233
    ));

const decisionMetadata = decision => ({
  role: decision.role,
  title: decision.title,
  searchable: decision.searchable,
  filterable: decision.filterable,
  display: decision.display
});

export default function DescriptionParameterEditor({
  open,
  item,
  decision,
  groups,
  independentTarget,
  disabled,
  normalizationEnabled,
  valuePreviewApi,
  tr,
  closeLabel,
  cancelLabel,
  createRoleDecision,
  onClose,
  onCommit
}) {
  const [draft, setDraft] = useState(null);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [valueSeparatorChoice, setValueSeparatorChoice] = useState("single");

  useEffect(() => {
    if (!open || !item) return;
    setDraft(decision ? { ...decision } : null);
    setValueSeparatorChoice(
      valueSeparators.includes(decision?.value_separator)
        ? decision.value_separator
        : decision?.value_separator != null
        ? "custom"
        : "single"
    );
    setDiscardOpen(false);
  }, [decision, item, open]);

  const initialSnapshot = JSON.stringify(decision || null);
  const currentSnapshot = JSON.stringify(draft || null);
  const dirty = initialSnapshot !== currentSnapshot;
  const requestClose = () => (dirty ? setDiscardOpen(true) : onClose());

  const resolvedIndependentTarget =
    draft && !groups[draft.target_key] ? draft.target_key : independentTarget;
  const compatibleGroups = useMemo(() => {
    if (!draft || draft.role === "ignore") return [];
    return Object.entries(groups || {}).filter(
      ([key, group]) =>
        key !== resolvedIndependentTarget && group.role === draft.role
    );
  }, [draft, groups, resolvedIndependentTarget]);
  const mergeOptions = useMemo(
    () => [
      {
        key: resolvedIndependentTarget,
        title: tr("independent")
      },
      ...compatibleGroups.map(([key, group]) => ({
        key,
        title: group.title
      }))
    ],
    [compatibleGroups, resolvedIndependentTarget, tr]
  );
  const selectedMergeOption =
    mergeOptions.find(option => option.key === draft?.target_key) ||
    mergeOptions[0] ||
    null;
  const splitPreview = useMemo(() => {
    const separator = draft?.value_separator;
    if (!separator || !validValueSeparator(separator)) return [];
    return (item?.examples || [])
      .slice(0, 5)
      .flatMap(value => value.split(separator))
      .map(value => value.trim())
      .filter(Boolean)
      .filter((value, index, values) => values.indexOf(value) === index)
      .slice(0, 8);
  }, [item, draft?.value_separator]);

  const chooseRole = role => {
    const next = createRoleDecision(role, draft);
    if (role === "ignore") {
      next.value_separator = null;
      setValueSeparatorChoice("single");
    }
    setDraft(next);
  };
  const patchDraft = patch =>
    setDraft(current => {
      const next = { ...current, ...patch };
      if (next.role === "identifier") next.filterable = false;
      return next;
    });
  const selectTarget = targetKey => {
    if (targetKey === resolvedIndependentTarget) {
      patchDraft({ target_key: resolvedIndependentTarget });
      return;
    }
    const group = groups[targetKey];
    patchDraft({ ...decisionMetadata(group), target_key: targetKey });
  };
  const finish = () => {
    if (!draft) return;
    const next = { ...draft };
    onCommit({
      decision: next,
      groupMetadata: next.role === "ignore" ? null : decisionMetadata(next)
    });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={requestClose}
        fullWidth
        maxWidth="md"
        transitionDuration={{ enter: 160, exit: 40 }}
        PaperProps={{ className: styles.dialogPaper }}
        aria-labelledby="description-field-editor-title"
      >
        {item && (
          <>
            <div className={styles.dialogTitle}>
              <div className={styles.dialogTitleCopy}>
                <h2 id="description-field-editor-title">
                  {tr("reviewField", { label: item.label })}
                </h2>
                <p>
                  {tr("fieldStats", {
                    products: item.product_count,
                    values: item.distinct_value_count
                  })}
                </p>
              </div>
              <IconButton aria-label={closeLabel} onClick={requestClose}>
                <CloseIcon />
              </IconButton>
            </div>
            <DialogContent className={styles.dialogContent}>
              <div className={styles.editorGrid}>
                <div className={styles.editorSection}>
                  <div>
                    <span className={styles.controlLabel}>
                      {tr("useFieldAs")}
                    </span>
                    <div
                      className={styles.roleChoices}
                      role="group"
                      aria-label={tr("useFieldAs")}
                    >
                      {[
                        [
                          "parameter",
                          "filterRole",
                          "filterRoleHelp",
                          "filterRoleChoice",
                          ParameterFilterIcon
                        ],
                        [
                          "identifier",
                          "searchable",
                          "searchableRoleHelp",
                          "searchOnlyChoice",
                          SearchIcon
                        ],
                        [
                          "ignore",
                          "ignore",
                          "ignoreRoleHelp",
                          "ignoreChoice",
                          VisibilityOffOutlinedIcon
                        ]
                      ].map(([role, label, help, choice, Icon]) => (
                        <button
                          type="button"
                          key={role}
                          className={styles.roleChoice}
                          aria-label={tr(choice)}
                          aria-pressed={draft?.role === role}
                          disabled={disabled}
                          onClick={() => chooseRole(role)}
                        >
                          <span className={styles.roleChoiceCopy}>
                            <span className={styles.roleChoiceHeading}>
                              {draft?.role === role ? (
                                <CheckIcon
                                  aria-hidden="true"
                                  data-testid="description-role-selected"
                                />
                              ) : (
                                <Icon aria-hidden="true" />
                              )}
                              <strong>{tr(label)}</strong>
                            </span>
                          </span>
                        </button>
                      ))}
                    </div>
                    {draft && (
                      <p className={styles.roleSummary}>
                        {tr(
                          draft.role === "parameter"
                            ? "filterRoleHelp"
                            : draft.role === "identifier"
                            ? "searchableRoleHelp"
                            : "ignoreRoleHelp"
                        )}
                      </p>
                    )}
                  </div>

                  {draft && draft.role !== "ignore" && (
                    <>
                      <TextField
                        id={`title-${item.field_id}`}
                        InputProps={{ classes: inputClasses }}
                        fullWidth
                        label={tr("displayTitle")}
                        value={draft.title}
                        inputProps={{ maxLength: 512 }}
                        disabled={disabled}
                        variant="outlined"
                        size="small"
                        onChange={event =>
                          patchDraft({ title: event.target.value })
                        }
                      />
                      <Autocomplete
                        className={styles.mergePicker}
                        classes={{
                          paper: styles.mergeMenuPaper,
                          option: styles.mergeOption
                        }}
                        options={mergeOptions}
                        value={selectedMergeOption}
                        getOptionLabel={option => option.title || ""}
                        getOptionSelected={(option, value) =>
                          option.key === value.key
                        }
                        noOptionsText={tr("mergeNoOptions")}
                        autoHighlight
                        disableClearable
                        disabled={disabled}
                        onChange={(_event, option) =>
                          option && selectTarget(option.key)
                        }
                        renderInput={params => (
                          <TextField
                            {...params}
                            InputProps={{
                              ...params.InputProps,
                              classes: inputClasses
                            }}
                            id={`combine-${item.field_id}`}
                            fullWidth
                            label={tr("mergeWith")}
                            placeholder={tr("mergeSearch")}
                            variant="outlined"
                            size="small"
                          />
                        )}
                      />

                      <div className={styles.valueFormat}>
                        <TextField
                          id="description-value-separator-kind"
                          InputProps={{ classes: inputClasses }}
                          select
                          fullWidth
                          label={tr("valueFormat")}
                          value={valueSeparatorChoice}
                          onChange={event => {
                            const choice = event.target.value;
                            setValueSeparatorChoice(choice);
                            patchDraft({
                              value_separator:
                                choice === "single"
                                  ? null
                                  : choice === "custom"
                                  ? ""
                                  : choice
                            });
                          }}
                          disabled={disabled}
                          variant="outlined"
                          size="small"
                          SelectProps={{
                            MenuProps: {
                              getContentAnchorEl: null,
                              classes: { paper: styles.mergeMenuPaper }
                            }
                          }}
                        >
                          <MenuItem
                            className={styles.valueFormatOption}
                            value="single"
                          >
                            {tr("singleValue")}
                          </MenuItem>
                          {valueSeparators.map((value, index) => (
                            <MenuItem
                              className={styles.valueFormatOption}
                              key={value}
                              value={value}
                            >
                              {tr(
                                [
                                  "commaSeparated",
                                  "semicolonSeparated",
                                  "verticalBarSeparated"
                                ][index]
                              )}
                            </MenuItem>
                          ))}
                          <MenuItem
                            className={styles.valueFormatOption}
                            value="custom"
                          >
                            {tr("customSeparatorOption")}
                          </MenuItem>
                        </TextField>
                        {valueSeparatorChoice === "custom" && (
                          <TextField
                            id="description-value-separator-custom"
                            InputProps={{ classes: inputClasses }}
                            fullWidth
                            label={tr("customSeparator")}
                            value={draft.value_separator}
                            onChange={event =>
                              patchDraft({
                                value_separator: event.target.value
                              })
                            }
                            disabled={disabled}
                            error={!validValueSeparator(draft.value_separator)}
                            inputProps={{ maxLength: 32 }}
                            variant="outlined"
                            size="small"
                          />
                        )}
                        {draft.value_separator != null &&
                          splitPreview.length > 0 && (
                            <div
                              className={styles.splitPreview}
                              data-testid="split-value-preview"
                            >
                              <span>{tr("splitPreview")}</span>
                              <div className={styles.splitPreviewValues}>
                                {splitPreview.map((value, index) => (
                                  <strong key={`${value}-${index}`}>
                                    {value}
                                  </strong>
                                ))}
                              </div>
                            </div>
                          )}
                      </div>

                      {normalizationEnabled && (
                        <DescriptionParameterNormalization
                          decision={draft}
                          examples={item.examples || []}
                          disabled={disabled}
                          onChange={patchDraft}
                          api={valuePreviewApi}
                          tr={tr}
                        />
                      )}
                      <FormControlLabel
                        className={styles.outputChoice}
                        label={tr("showOnProductCard")}
                        control={
                          <Checkbox
                            color="primary"
                            checked={draft.display}
                            disabled={disabled}
                            inputProps={{
                              "aria-label": tr("showOnProductCardField", {
                                label: item.label
                              })
                            }}
                            onChange={event =>
                              patchDraft({ display: event.target.checked })
                            }
                          />
                        }
                      />
                    </>
                  )}
                </div>

                <aside className={styles.evidence}>
                  <h3>{tr("examples")}</h3>
                  <ul>
                    {(item.examples || []).map((value, index) => (
                      <li key={`${item.field_id}-evidence-${index}`}>
                        {value}
                      </li>
                    ))}
                  </ul>
                </aside>
              </div>
            </DialogContent>
            <DialogActions className={styles.editorActions}>
              <Button onClick={requestClose}>{cancelLabel}</Button>
              <Button
                color="primary"
                variant="contained"
                disabled={
                  !draft ||
                  disabled ||
                  !validValueSeparator(draft.value_separator)
                }
                onClick={finish}
              >
                {tr("done")}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      <DescriptionParametersConfirmDialog
        open={discardOpen}
        title={tr("discardTitle")}
        body={tr("discardHelp")}
        closeLabel={closeLabel}
        discardLabel={tr("discard")}
        keepLabel={tr("keep")}
        onDiscard={() => {
          setDiscardOpen(false);
          onClose();
        }}
        onKeep={() => setDiscardOpen(false)}
      />
    </>
  );
}

DescriptionParameterEditor.propTypes = {
  open: PropTypes.bool.isRequired,
  item: PropTypes.shape({
    field_id: PropTypes.string,
    label: PropTypes.string,
    product_count: PropTypes.number,
    distinct_value_count: PropTypes.number,
    examples: PropTypes.arrayOf(PropTypes.string)
  }),
  decision: PropTypes.shape({
    role: PropTypes.string,
    target_key: PropTypes.string,
    title: PropTypes.string,
    searchable: PropTypes.bool,
    filterable: PropTypes.bool,
    display: PropTypes.bool,
    value_aliases: PropTypes.objectOf(PropTypes.string),
    value_separator: PropTypes.string
  }),
  groups: PropTypes.objectOf(PropTypes.object),
  independentTarget: PropTypes.string,
  disabled: PropTypes.bool,
  normalizationEnabled: PropTypes.bool,
  valuePreviewApi: PropTypes.func,
  tr: PropTypes.func.isRequired,
  closeLabel: PropTypes.string.isRequired,
  cancelLabel: PropTypes.string.isRequired,
  createRoleDecision: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onCommit: PropTypes.func.isRequired
};

DescriptionParameterEditor.defaultProps = {
  item: null,
  decision: null,
  groups: {},
  independentTarget: "",
  disabled: false
};
